<template>
  <div :class="{ '!w-full': attrs.full_width }">
    <DataTableSkeleton v-if="isLoading"></DataTableSkeleton>
    <DataTableVue v-else-if="dataSet.length > 0" :data-set="dataSet">
      <template #headerCell="{ label }">
        <span v-if="label == 'shipSelector'"></span>
      </template>
      <template #cell="{ columnName, columnValue, rowIndex }">
        <div v-if="columnName == 'shipSelector'" class="w-full h-full flex gap-1">
          <button
            class="aspect-square size-5 rounded-full border-2 border-primary p-0.5"
            @click.prevent="selectShip(rowIndex)"
          >
            <div
              :class="{
                'bg-primary': formStore.extraMap.selectedShipIndex == rowIndex
              }"
              class="w-full h-full aspect-square rounded-full"
            ></div>
          </button>
        </div>
      </template>
    </DataTableVue>
    <div v-else class="w-full p-2 bg-brand-gray-250 text-center">
      {{ $t('noDataFound') }}
    </div>
  </div>
</template>

<script setup>
import DataTableVue from '@/components/DataTable/index.vue'
import DataTableSkeleton from '@/components/skeletons/DatatableSkeleton.vue'
import { useAuthStore } from '@/stores/auth'
import { useFormStore } from '@/stores/form'
import { useLookupsStore } from '@/stores/lookups'
import { onMounted, watch, ref, useAttrs } from 'vue'
import lodashGet from 'lodash/get'

const attrs = useAttrs()
const isLoading = ref(true)

const dataSet = ref([])
const authStore = useAuthStore()
const lookupStore = useLookupsStore()
const formStore = useFormStore()
const countries = ref()

async function buildRelatedPersons() {
  const establishmentEmployeeTypes = await lookupStore.fetchLookup(
    '/api/lookup/establishmentEmployeeTypes'
  )

  if (!authStore.establishmentInfo) {
    await authStore.fetchEstablishmentInfo()
  }

  const { relatedPerson } = authStore.establishmentInfo

  return relatedPerson.map((person) => {
    return {
      identityNumber: person.identityNumber,
      englishFullName: person.englishName,
      arabicFullName: person.arabicName,
      nationalityId: countries.value.find((c) => c.id == person.nationalityId)?.localizedName,
      employeeTypeId: establishmentEmployeeTypes.find((eet) => eet.id == person.employeeTypeId)
        ?.localizedName,
      description: person.unifiedNumber
    }
  })
}

async function buildCurrentServices() {
  const currentServices = await lookupStore.fetchLookup('/api/establishment/getCurrentServices', {
    estabId: authStore.establishmentInfo.id
  })

  if ((currentServices || []).length === 0) {
    return []
  }

  return currentServices.collection.map((col) => {
    return {
      TheCurrentServices: col.lookupValidGetwayService.text
    }
  })
}

async function buildEntryOrExitTransactions(data) {
  if (!data) {
    return []
  }
  return data.map((col) => {
    return {
      ...col,
      countryPort: col.countryPort.text,
      currentNationality: col.currentNationality.text,
      destinationCountry: col.destinationCountry.text,
      portType: col.portType.text,
      travelPassportType: col.travelPassportType.text,
      travelTransactionType: col.travelTransactionType.text
    }
  })
}

async function buildAccompaniesTable(data) {
  if (!data) {
    return []
  }
  return data.map((col) => {
    return {
      unifiedNumber: col.personUnifiedNumber,
      name: col.englishName + col.arabicName,
      gender: col.genderId,
      religion: col.religionId,
      faith: col.faithId,
      relation: col.relationshipId,
      nationality: col.nationalityId,
      birthCountry: col.birthCountryId,
      dateOfBirth: col.dateOfBirth
    }
  })
}

async function buildRegisteredShips() {
  const formStore = useFormStore()
  const { registeredShips = [] } = formStore.extraMap

  if (registeredShips.length === 0) return []

  return registeredShips.map((rs) => {
    return {
      shipSelector: '',
      ...rs
    }
  })
}

async function selectShip(shipIndex) {
  const { registeredShips } = formStore.extraMap
  const { shipNumber, nationalityId: shipNationalityId, shipTypeId } = registeredShips[shipIndex]

  Object.assign(formStore.extraMap, {
    selectedShipIndex: shipIndex
  })

  formStore.form.setValues({
    shipNumber,
    shipNationalityId,
    shipTypeId
  })

  Object.assign(formStore.draft.request, {
    shipNumber,
    shipNationalityId,
    shipTypeId
  })
}

const builderFuncs = {
  buildRelatedPersons,
  buildCurrentServices,
  buildRegisteredShips,
  buildEntryOrExitTransactions,
  buildAccompaniesTable
}

onMounted(async () => {
  countries.value = await lookupStore.fetchLookup('/api/lookup/countries')
  if ('builder_func' in attrs) {
    dataSet.value = await builderFuncs[attrs.builder_func]()
  }

  isLoading.value = false
})

watch(
  () => lodashGet(formStore.form.values, attrs.dependent_on),
  async (newValue) => {
    dataSet.value = await builderFuncs[attrs.builder_func](newValue)
  }
)
</script>

<script>
export default {
  name: 'DataTableForm'
}
</script>
<style lang="scss" scoped></style>
